import type { OrderDto } from '@pl/domain';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { CHANNEL_DOT, channelName } from '../../lib/format';
import { useOrders, useSiteMap, useSites, useSkuMap, useSkus } from '../../lib/queries';
import { Bar, Chip, ErrorNote, RefetchNote, SiteTag, Spinner, Swatch, shortName } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { useSiteScope } from '../useSiteScope';
import { OrderDrawer } from './OrderDrawer';
import s from './Orders.module.css';

type TabKey = 'all' | 'open' | 'picking' | 'ready' | 'shipped' | 'cancelled';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All active' },
  { key: 'open', label: 'Open' },
  { key: 'picking', label: 'Picking' },
  { key: 'ready', label: 'Ready' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'cancelled', label: 'Canceled' },
];

/** externalRef ?? code, per the plan's "Changes from Plan 2's final review" — never a raw id. */
export function orderRef(o: Pick<OrderDto, 'externalRef' | 'code'>): string {
  return o.externalRef ?? o.code ?? '—';
}

/** "Ready" isn't an API status — it's picking with every box scanned, computed on the client. */
export function isReady(o: Pick<OrderDto, 'status' | 'scanned' | 'totalQty'>): boolean {
  return o.status === 'picking' && o.scanned === o.totalQty;
}

export function progTone(o: Pick<OrderDto, 'status' | 'scanned' | 'totalQty'>): 'ok' | 'accent' | undefined {
  if (isReady(o)) return 'ok';
  if (o.scanned > 0) return 'accent';
  return undefined;
}

export function statusChip(o: Pick<OrderDto, 'status' | 'scanned' | 'totalQty'>) {
  if (o.status === 'open') return <Chip>Open</Chip>;
  if (o.status === 'picking') return isReady(o) ? (
    <Chip tone="ok" dot>
      Ready
    </Chip>
  ) : (
    <Chip tone="accent">Picking</Chip>
  );
  if (o.status === 'shipped') return <Chip tone="ink">Shipped</Chip>;
  return <Chip tone="bad">Cancelled</Chip>;
}

export function shipByLabel(requiredBy: string | null): { text: string; today: boolean } {
  if (!requiredBy) return { text: '—', today: false };
  const today = new Date().toLocaleDateString('en-CA');
  if (requiredBy === today) return { text: 'Today', today: true };
  return { text: new Date(`${requiredBy}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), today: false };
}

export function Orders() {
  const { id } = useParams<{ id?: string }>();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { siteId, setSiteId } = useSiteScope();
  const sitesQuery = useSites();
  const skusQuery = useSkus();
  const siteMap = useSiteMap();
  const skuMap = useSkuMap();

  const tab = (searchParams.get('tab') as TabKey | null) ?? 'all';
  const q = searchParams.get('q') ?? '';

  const activeQuery = useOrders({ siteId, search: q || undefined, status: ['open', 'picking'], limit: 200 });
  const shippedQuery = useOrders({ siteId, search: q || undefined, status: ['shipped'], limit: 50 });
  const cancelledQuery = useOrders({ siteId, search: q || undefined, status: ['cancelled'], limit: 50 });

  const active = activeQuery.data ?? [];
  const shipped = shippedQuery.data ?? [];
  const cancelled = cancelledQuery.data ?? [];

  const rowsByTab = useMemo<Record<TabKey, OrderDto[]>>(
    () => ({
      all: active,
      open: active.filter((o) => o.status === 'open'),
      picking: active.filter((o) => o.status === 'picking'),
      ready: active.filter(isReady),
      shipped,
      cancelled,
    }),
    [active, shipped, cancelled],
  );

  const rows = rowsByTab[tab];

  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true },
    );
  }

  function setQuery(v: string) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (v) p.set('q', v);
        else p.delete('q');
        return p;
      },
      { replace: true },
    );
  }

  function closeDrawer() {
    nav({ pathname: '/office/orders', search: location.search });
  }

  // Only a query with nothing to show takes the list. A failed background refresh keeps the rows it
  // already has and says so above them.
  const listQueries = [sitesQuery, skusQuery, activeQuery, shippedQuery, cancelledQuery];
  const failedQueries = listQueries.filter((qr) => qr.isLoadingError);
  const loaded = Boolean(sitesQuery.data && skusQuery.data && activeQuery.data && shippedQuery.data && cancelledQuery.data);

  function renderList(): ReactNode {
    if (failedQueries.length > 0) {
      return (
        <div className="panel">
          <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
            <ErrorNote error={failedQueries[0]!.error} />
            <button type="button" className="btn" onClick={() => failedQueries.forEach((qr) => qr.refetch())}>
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (!loaded) {
      return (
        <div className="row">
          <Spinner label="Loading orders" />
        </div>
      );
    }

    const sites = sitesQuery.data!;
    const activeTabLabel = TABS.find((t) => t.key === tab)?.label.toLowerCase() ?? 'orders';

    return (
      <div className={s.list}>
        <RefetchNote queries={listQueries} />
        <nav className="tabs" role="tablist" aria-label="Order status">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={tab === t.key ? 'on' : undefined}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <span className="n">{rowsByTab[t.key].length}</span>
            </button>
          ))}
        </nav>

        <div className="fbar">
          <div className="seg">
            <button type="button" className={siteId === undefined ? 'on' : undefined} aria-pressed={siteId === undefined} onClick={() => setSiteId(undefined)}>
              All sites
            </button>
            {sites.map((site) => (
              <button key={site.id} type="button" className={siteId === site.id ? 'on' : undefined} aria-pressed={siteId === site.id} onClick={() => setSiteId(site.id)}>
                <span className="code">{site.shortCode}</span>
                {site.name}
              </button>
            ))}
          </div>
          <label className="search">
            <Icon name="search" />
            <input
              type="text"
              placeholder="Order #, customer or SKU"
              value={q}
              onChange={(e) => setQuery(e.target.value)}
              style={{ border: 0, outline: 'none', font: 'inherit', color: 'inherit', background: 'transparent', flex: 1, minWidth: 0 }}
            />
          </label>
          <span className="grow" />
        </div>

        <div className="panel">
          <table className={`t ${s.t}`}>
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Site</th>
                <th>Ship by</th>
                <th>Lines</th>
                <th>Scanned</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const when = shipByLabel(o.requiredBy);
                return (
                  <tr key={o.id} className={id === o.id ? `sel ${s.sel}` : undefined}>
                    <td>
                      <div className={s.ord}>
                        <Link className="mono" to={{ pathname: `/office/orders/${o.id}`, search: location.search }}>
                          {orderRef(o)}
                        </Link>
                        <span className="channel">
                          <i style={{ background: CHANNEL_DOT[o.channel] }} />
                          {channelName(o.channel)}
                        </span>
                      </div>
                    </td>
                    <td className={s.cust}>{o.customerName}</td>
                    <td>
                      <SiteTag site={siteMap.get(o.siteId)} />
                    </td>
                    <td className={`${s.when}${when.today ? ` ${s.today}` : ''}`}>{when.text}</td>
                    <td>
                      <div className="lines v">
                        {o.lines.map((l) => {
                          const sku = skuMap.get(l.skuId);
                          return (
                            <Chip key={l.id}>
                              <Swatch hex={sku?.colourHex ?? null} name={sku?.colourName ?? null} />
                              {shortName(sku?.name ?? 'Blank sticker')} ×{l.qty}
                            </Chip>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <div className={s.prog}>
                        <Bar value={o.scanned} max={o.totalQty} tone={progTone(o)} />
                        <span>
                          {o.scanned}/{o.totalQty}
                        </span>
                      </div>
                    </td>
                    <td>{statusChip(o)}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted" style={{ padding: 20, textAlign: 'center' }}>
                    No orders match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className={`panel-f ${s.panelF}`}>
            <span>
              {rows.length} {activeTabLabel}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {renderList()}
      <OrderDrawer id={id} onClose={closeDrawer} />
    </>
  );
}
